import { Classes, Icon, IconName } from '@clarion-blueprint-shim';
import { ReactElement, useCallback } from 'react';
import {
  Text,
  UNSTABLE_Toast as Toast,
  UNSTABLE_ToastContent as ToastContent,
} from 'react-aria-components';
import { QueuedToast } from 'react-stately';
import { tv } from 'tailwind-variants';
import { cx } from '../util';
import { ClarionButton } from './ClarionButton';
import { ClarionButtonLink } from './ClarionButtonLink';
import { ToastProps } from './toast-types';

export interface ClarionToastComponentProps {
  toast: QueuedToast<ToastProps>;
  onClose: (key: string) => void;
  className?: string;
}

const toastStyles = tv({
  base: [
    // 'flex items-start gap-3 p-4 rounded-lg shadow-lg border max-w-md min-w-80',
    // 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-600',
    'animate-in slide-in-from-right-full fade-in duration-300',
    'data-[exiting]:animate-out data-[exiting]:slide-out-to-right-full data-[exiting]:fade-out data-[exiting]:duration-200',
    'forced-colors:border-[ButtonBorder] forced-colors:border-3',
    Classes.TOAST,
  ],
  variants: {
    intent: {
      none: '',
      primary: Classes.INTENT_PRIMARY,
      success: Classes.INTENT_SUCCESS,
      warning: Classes.INTENT_WARNING,
      danger: Classes.INTENT_DANGER,
    },
  },
});

const iconStyles = tv({
  base: 'flex-shrink-0 mt-0.5',
  variants: {
    intent: {
      none: 'text-gray-500 dark:text-gray-400',
      primary: 'text-blue-600 dark:text-blue-400',
      success: 'text-green-600 dark:text-green-400',
      warning: 'text-orange-600 dark:text-orange-400',
      danger: 'text-red-600 dark:text-red-400',
    },
  },
});

function getDefaultIcon(intent: string): IconName {
  switch (intent) {
    case 'success':
      return 'tick-circle';
    case 'warning':
      return 'warning-sign';
    case 'danger':
      return 'error';
    case 'primary':
      return 'info-sign';
    default:
      return 'blank';
  }
}

function isIconName(icon: IconName | ReactElement): icon is IconName {
  return typeof icon === 'string';
}

export function ClarionToastComponent({ toast, onClose, className }: ClarionToastComponentProps) {
  const { content } = toast;
  const intent = content.intent || 'none';
  const isCloseButtonShown = content.isCloseButtonShown ?? true;

  const handleDismiss = useCallback(() => {
    onClose(toast.key);
  }, [onClose, toast.key]);

  const handleActionClick = (e: any) => {
    content.action?.onClick?.(e);
    handleDismiss();
  };

  const displayIcon = content.icon || (intent !== 'none' ? getDefaultIcon(intent) : undefined);

  return (
    <Toast toast={toast} className={toastStyles({ intent, className })}>
      <ToastContent className={cx(Classes.TOAST_MESSAGE, 'pl-4! flex gap-2 items-center')}>
        {displayIcon && (
          <div className={iconStyles({ intent })}>
            {isIconName(displayIcon) ? <Icon icon={displayIcon} /> : displayIcon}
          </div>
        )}
        <Text slot="title" className="grow">
          {content.message}
        </Text>

        {content.action && (
          <div className="shrink-0">
            {content.action.href ? (
              <ClarionButtonLink
                variant="minimal"
                size="small"
                {...content.action}
                children={content.action.text}
                onClick={handleActionClick}
              />
            ) : (
              <ClarionButton
                variant="minimal"
                size="small"
                {...content.action}
                children={content.action.text}
                onClick={handleActionClick}
              />
            )}
          </div>
        )}

        {isCloseButtonShown && (
          <ClarionButton icon="cross" variant="minimal" onPress={handleDismiss} slot="close" />
        )}
      </ToastContent>
    </Toast>
  );
}
